'use client'
import {useState, useMemo} from 'react'
import {
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import {Event} from './Event'
import {DateTime} from 'luxon'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {EventType} from '../types'
import {doppler} from './font'

const COMPETITION_FILTERS = [
  'All',
  'Pre-season',
  'Premier League',
  'UEFA Champions League',
  'FA Cup',
  'Carabao Cup',
] as const

// Competitions grouped under the single "Pre-season" chip
const PRE_SEASON_COMPETITIONS = [
  'Pre-season Friendly',
  'Emirates Cup',
  'FA Community Shield',
]

// A match stays under Upcoming until this long after kickoff, so it stays
// visible while it is still being played
const MATCH_WINDOW_HOURS = 2

type FilterType = (typeof COMPETITION_FILTERS)[number]

export const AllEvents = ({events, photoMatchMap = {}}: {events: EventType[]; photoMatchMap?: Record<string, string>}) => {
  const [filter, setFilter] = useState<FilterType>('All')

  const parseDate = (dateUtc: string) =>
    DateTime.fromFormat(dateUtc, "yyyy-MM-dd HH:mm:ss'Z'", {
      zone: 'utc',
    }).setZone('America/New_York')

  const {upcomingEvents, pastEvents} = useMemo(() => {
    const now = DateTime.now().setZone('America/New_York')
    const upcoming: EventType[] = []
    const past: EventType[] = []

    events.forEach((event) => {
      const eventDate = parseDate(event.DateUtc)
      if (eventDate.plus({hours: MATCH_WINDOW_HOURS}) > now) {
        upcoming.push(event)
      } else {
        past.push(event)
      }
    })

    upcoming.sort(
      (a, b) => parseDate(a.DateUtc).toMillis() - parseDate(b.DateUtc).toMillis()
    )
    past.sort(
      (a, b) => parseDate(b.DateUtc).toMillis() - parseDate(a.DateUtc).toMillis()
    )

    return {upcomingEvents: upcoming, pastEvents: past}
  }, [events])

  const filterEvents = (list: EventType[]) => {
    if (filter === 'All') return list
    if (filter === 'Pre-season')
      return list.filter(
        (e) => e.competition && PRE_SEASON_COMPETITIONS.includes(e.competition)
      )
    return list.filter((e) => e.competition === filter)
  }

  const filteredUpcoming = filterEvents(upcomingEvents)
  const filteredPast = filterEvents(pastEvents)

  return (
    <Container id="matches">
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: {xs: 6, md: 8}, pb: {xs: 8, md: 12}}}>
        {/* Competition filter chips */}
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
          {COMPETITION_FILTERS.map((comp) => (
            <Chip
              key={comp}
              label={comp}
              onClick={() => setFilter(comp)}
              variant={filter === comp ? 'filled' : 'outlined'}
              sx={{
                backgroundColor:
                  filter === comp ? '#DB0007' : 'transparent',
                color: filter === comp ? '#fff' : '#A1A1AA',
                borderColor: '#3A3A46',
                '&:hover': {
                  backgroundColor:
                    filter === comp ? '#FF1A22' : 'rgba(219, 0, 7, 0.08)',
                },
              }}
            />
          ))}
        </Box>

        {/* UPCOMING MATCHES */}
        <Accordion
          defaultExpanded={true}
          sx={{
            bgcolor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
            mt: 4,
          }}
        >
          <AccordionSummary
            sx={{padding: 0}}
            expandIcon={<ExpandMoreIcon sx={{color: '#DB0007'}} />}
            aria-controls="upcoming-matches-content"
            id="upcoming-matches-header"
          >
            <Box>
              <Typography
                component="h2"
                sx={{
                  fontFamily: doppler.style.fontFamily,
                  fontWeight: 700,
                  fontSize: {xs: '1.5rem', md: '2rem'},
                  letterSpacing: '0.02em',
                  textTransform: 'lowercase',
                  color: 'text.primary',
                  margin: 0,
                }}
              >
                Upcoming Matches
              </Typography>
              <Box
                sx={{
                  width: 80,
                  height: 2,
                  bgcolor: '#DB0007',
                  mt: 1,
                }}
              />
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{padding: 0}}>
            <Grid container spacing={2} mt={2}>
              {filteredUpcoming.length ? (
                filteredUpcoming.map((event, index) => (
                  <Event
                    key={`${event.DateUtc}-${event.HomeTeam}-${event.AwayTeam}`}
                    index={index}
                    event={event}
                  />
                ))
              ) : (
                <Typography
                  variant="h5"
                  gutterBottom
                  color="text.secondary"
                  ml={1}
                >
                  No upcoming matches
                </Typography>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* RECENT RESULTS */}
        {filteredPast.length > 0 && (
          <Accordion
            defaultExpanded={true}
            sx={{
              bgcolor: 'transparent',
              backgroundImage: 'none',
              boxShadow: 'none',
              mt: 4,
            }}
          >
            <AccordionSummary
              sx={{padding: 0}}
              expandIcon={<ExpandMoreIcon sx={{color: '#DB0007'}} />}
              aria-controls="past-results-content"
              id="past-results-header"
            >
              <Box>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: doppler.style.fontFamily,
                    fontWeight: 700,
                    fontSize: {xs: '1.5rem', md: '2rem'},
                    letterSpacing: '0.02em',
                    textTransform: 'lowercase',
                    color: 'text.primary',
                    display: 'block',
                  }}
                >
                  Past Matches
                </Typography>
                <Box
                  sx={{
                    width: 80,
                    height: 2,
                    bgcolor: '#DB0007',
                    mt: 1,
                  }}
                />
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{padding: 0}}>
              <Grid container spacing={2} mt={2}>
                {filteredPast.map((event, index) => {
                  const opp = event.AwayTeam === 'Arsenal' ? event.HomeTeam : event.AwayTeam
                  const date = event.DateUtc.split(' ')[0]
                  const normalizedOpp = ({'bayern münchen': 'bayern munich', 'atleti': 'atletico madrid'} as Record<string, string>)[opp.toLowerCase()] ?? opp.toLowerCase()
                  const photoMatchId = photoMatchMap[`${normalizedOpp}|${date}`]
                  return (
                    <Event
                      key={`${event.DateUtc}-${event.HomeTeam}-${event.AwayTeam}`}
                      index={index}
                      event={event}
                      past
                      photoMatchId={photoMatchId}
                    />
                  )
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Container>
  )
}

